import ProfileAvatar from '-/Custom/Avatar';
import { createContext, type ReactNode, use } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Item, { type PressableRowProps } from '@/components/UI/PressableRow';
import StaticRow, { type RowProps } from '@/components/UI/Row';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import { type FragmentOf, readFragment } from '@/gql/graphql';
import useCurrentUser from '@/hooks/useCurrentUser';
import usePersonalizeName from '@/hooks/usePersonalizeName';
import { useChatStore } from '@/stores/useChatStore';
import formatChatHomeDateTime from '@/utils/formatChatHomeDatetime';
import isInternalLink from '@/utils/isInternalLink';
import { resolveMessage } from '@/utils/messageMentions';
import { ChatHomeListItemFragment } from './ChatHomeListItem.utils';
import { ChatMessageItemFragment } from './ChatMessageItem.utils';
import ChatTypeLabel from './ChatTypeLabel';

const ChatHomeListItemContext = createContext<FragmentOf<
  typeof ChatHomeListItemFragment
> | null>(null);

function useChatHomeListItem() {
  const context = use(ChatHomeListItemContext);

  if (!context) {
    throw new Error('ChatHomeListItem parts must be inside its Provider.');
  }

  return readFragment(ChatHomeListItemFragment, context);
}

function Provider({
  children,
  fragment,
}: {
  children: ReactNode;
  fragment: FragmentOf<typeof ChatHomeListItemFragment>;
}) {
  return (
    <ChatHomeListItemContext value={fragment}>
      {children}
    </ChatHomeListItemContext>
  );
}

interface PressableProps extends Omit<PressableRowProps, 'children' | 'style'> {
  children: ReactNode;
  itemStyle?: StyleProp<ViewStyle>;
}

function Pressable({ children, itemStyle, ...itemProps }: PressableProps) {
  return (
    <Item {...itemProps} style={itemStyle}>
      {children}
    </Item>
  );
}

function Row({ children, ...rowProps }: RowProps) {
  return <StaticRow {...rowProps}>{children}</StaticRow>;
}

function Avatar() {
  const item = useChatHomeListItem();
  const title = item.metadata?.title || '';

  return (
    <ProfileAvatar
      color={item.metadata?.color}
      name={title}
      size="md"
      id={item.metadata?.avatar_id || ''}
    />
  );
}

function Body({ children }: { children: ReactNode }) {
  return <Box flex>{children}</Box>;
}

function Header({ children }: { children: ReactNode }) {
  return (
    <Box flex direction="horizontal" alignItems="center" space="md">
      {children}
    </Box>
  );
}

function Identity() {
  const item = useChatHomeListItem();
  const title = item.metadata?.title || '';

  return (
    <Box flex flexShrink={1} direction="horizontal" alignItems="center">
      <Text flexShrink={1} numberOfLines={1}>
        {title}
      </Text>
      <Box direction="horizontal" alignItems="center" space="sm" pl="sm">
        <Box width={4} height={4} borderRadius={2} background="secondary" />
        <ChatTypeLabel chatType={item.chat_type} />
      </Box>
    </Box>
  );
}

function Status({ children }: { children: ReactNode }) {
  return (
    <Box direction="horizontal" alignItems="center" space="md">
      {children}
    </Box>
  );
}

function UnseenCount() {
  const { unseen_message_count: unseenMessageCount } = useChatHomeListItem();

  return unseenMessageCount ? (
    <Text
      style={style.unseenMessageCount}
      size="sm"
      variant="typographySecondary"
    >
      {unseenMessageCount}
    </Text>
  ) : null;
}

function SilencedStatus() {
  const { preference } = useChatHomeListItem();
  const user = useCurrentUser();
  const isSilenced =
    (preference?.notification_preference ??
      user?.chat_notification_preference ??
      'all') === 'none';

  return isSilenced ? (
    <Icon name="notifications-off-outline" size="sm" />
  ) : null;
}

function MessageDate() {
  const item = useChatHomeListItem();
  const message = readFragment(ChatMessageItemFragment, item.messages[0]);
  const datetime = message?.created_at;

  return datetime ? (
    <Text size="sm" variant="typographySecondary">
      {formatChatHomeDateTime(datetime)}
    </Text>
  ) : null;
}

function LastMessage() {
  const item = useChatHomeListItem();
  const draft = useChatStore((state) => state.chatInput[item.id]?.text.trim());
  const firstMessage = readFragment(ChatMessageItemFragment, item.messages[0]);
  const personalizeName = usePersonalizeName();
  const resolvedContent = resolveMessage(
    firstMessage?.content,
    firstMessage?.content_template,
    firstMessage?.mentions,
  ).plainText;
  const content = isInternalLink(resolvedContent)
    ? 'Sent a Link'
    : resolvedContent;
  const lastMessage = firstMessage?.creator?.name
    ? `${personalizeName(
        firstMessage.creator.id,
        firstMessage.creator.name,
      )}: ${content || 'Sent an image'}`
    : undefined;

  return draft || lastMessage ? (
    <Text
      variant="typographySecondary"
      size="sm"
      numberOfLines={2}
      italic={!!draft}
    >
      {draft ? `Draft: ${draft}` : lastMessage}
    </Text>
  ) : null;
}

const ChatHomeListItem = {
  Avatar,
  Body,
  Date: MessageDate,
  Header,
  Identity,
  LastMessage,
  Pressable,
  Provider,
  Row,
  SilencedStatus,
  Status,
  UnseenCount,
};

export default ChatHomeListItem;

const style = StyleSheet.create((theme) => ({
  unseenMessageCount: {
    backgroundColor: theme.colors.info,
    borderRadius: theme.borderRadius.full,
    paddingHorizontal: theme.size(2),
    color: theme.colors.white,
  },
}));
